import {
  BROWSER_APPSTORE,
  BROWSER_HOMEPAGES,
  BROWSER_PLAYSTORE,
  MINIMUM_SUPPORTED_BROWSER_VERSIONS,
} from '@/constants/common';

export function getDataTestId(value: string) {
  return {
    'data-test-id': value
      .toLowerCase()
      .replace(/([/\s]|undefined|null)+/g, '-')
      .replace(/[-]+/g, '-')
      .replace(/(^-|-$)/g, ''),
  };
}

export function saveLocal(key: string, value: unknown) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function readLocal(key: string) {
  const raw = localStorage.getItem(key);
  return raw ? JSON.parse(raw) : undefined;
}

export function removeLocal(key: string) {
  localStorage.removeItem(key);
}

export function saveSessionStorage(key: string, value: unknown) {
  sessionStorage.setItem(key, JSON.stringify(value));
}

export function readSessionStorage(key: string) {
  const raw = sessionStorage.getItem(key);
  if (!raw) {
    return undefined;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

export function removeSessionStorage(key: string) {
  sessionStorage.removeItem(key);
}

export function reloadPage() {
  window.location.reload();
}

export function isLandscape() {
  return window.innerWidth > window.innerHeight;
}

export function hasUserMedia() {
  return !!navigator.mediaDevices.getUserMedia;
}

export function getPlatform() {
  return {
    isAndroid: /Android/i.test(navigator.userAgent),
    isIOS: /iPhone|iPad|iPod/i.test(navigator.userAgent),
    isIPadOS:
      navigator.userAgent.match(/Mac/) &&
      navigator.maxTouchPoints &&
      navigator.maxTouchPoints > 2,
  };
}

export const appNavigator = (
  pathName: string,
  children: React.ReactElement,
) => {
  window.history.replaceState(null, 'Digital Onboarding', pathName);
  return children;
};

export const COOKIE_EXC_ENABLED = 'exc-enabled';
export const COOKIE_CONTINUE_NO_UPDATE_BROWSER = 'continue-no-update-browser';

const validTestCookiesName = [
  'test-beta',
  COOKIE_EXC_ENABLED,
  COOKIE_CONTINUE_NO_UPDATE_BROWSER,
];

export const getCookie = (name: string) => {
  if (!validTestCookiesName.includes(name)) return undefined;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
};

export const openICBLinkByPlatform = (links: {
  icbIOS: string;
  icbAndroid: string;
  icbWeb: string;
}) => {
  const { isAndroid, isIOS, isIPadOS } = getPlatform();

  if (isIOS || isIPadOS) {
    window.location.assign(links.icbIOS);
  } else if (isAndroid) {
    window.location.assign(links.icbAndroid);
  } else {
    window.location.assign(links.icbWeb);
  }
};

export const getBrowserMetadata = (userAgent: string) => {
  if (userAgent.includes('OPR/') || userAgent.includes('Opera/')) {
    const match =
      userAgent.match(/OPR\/(\d+)/) || userAgent.match(/Opera\/(\d+)\.(\d+)/);
    const version = match ? parseInt(match[1]) : 0;
    return { name: 'Opera', version };
  }

  if (userAgent.includes('SamsungBrowser')) {
    const match = userAgent.match(/SamsungBrowser\/(\d+)/);
    const version = match ? parseInt(match[1]) : 0;
    return { name: 'SamsungInternet', version };
  }

  if (
    userAgent.includes('Chrome') &&
    !userAgent.includes('Edg') &&
    !userAgent.includes('SamsungBrowser')
  ) {
    const match = userAgent.match(/Chrome\/(\d+)/);
    const version = match ? parseInt(match[1]) : 0;
    return { name: 'Chrome', version };
  }

  if (userAgent.includes('Firefox')) {
    const match = userAgent.match(/Firefox\/(\d+)/);
    const version = match ? parseInt(match[1]) : 0;
    return { name: 'Firefox', version };
  }

  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    const match = userAgent.match(/Version\/(\d+)/);
    const version = match ? parseInt(match[1]) : 0;
    return { name: 'Safari', version };
  }

  if (userAgent.includes('Edg')) {
    const match = userAgent.match(/Edg\/(\d+)/);
    const version = match ? parseInt(match[1]) : 0;
    return { name: 'Edge', version };
  }

  return { name: 'Unknown', version: 0 };
};

export const isBrowserSupported = (userAgent: string) => {
  const { name, version } = getBrowserMetadata(userAgent);
  const minVersion =
    MINIMUM_SUPPORTED_BROWSER_VERSIONS[
      name as keyof typeof MINIMUM_SUPPORTED_BROWSER_VERSIONS
    ];

  return minVersion ? version >= minVersion : false;
};

export const getBrowserDownloadLink = (userAgent: string) => {
  const { name } = getBrowserMetadata(userAgent);

  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return BROWSER_APPSTORE[name as keyof typeof BROWSER_APPSTORE];
  } else if (/Android/.test(userAgent)) {
    return BROWSER_PLAYSTORE[name as keyof typeof BROWSER_PLAYSTORE];
  }

  return BROWSER_HOMEPAGES[name as keyof typeof BROWSER_HOMEPAGES];
};
